import React, { useState } from 'react';
import { PALETTES, QUICK_ACCENTS, type PaletteId } from '../today/palettes';
import { cardLabel } from './unitLayout';

/** Customise tray for the unit page, same as the Today page's: colours, hidden cards to add back, reset, shared default. */
export function UnitCustomize(props: {
  hiddenIds: string[];
  onAdd: (id: string) => void;
  onReset: () => void;
  canReset: boolean;
  canSetDefault: boolean;
  onSaveDefault: () => Promise<void>;
  stacked: boolean;
  palette: PaletteId;
  accent: string | null;
  onColour: (patch: { palette?: PaletteId; accent: string | null }) => void;
}) {
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <div className="ot-panel-tray">
      <div className="ot-tray-row">
        <b>Colour</b>
        <div className="ot-seg wide" role="group" aria-label="Colour scheme">
          {PALETTES.map((p) => (
            <button key={p.id} aria-pressed={props.palette === p.id && !props.accent} onClick={() => props.onColour({ palette: p.id, accent: null })}>
              <span className="ot-sw" style={{ background: p.swatch, boxShadow: `inset -5px 0 0 ${p.panel}`, marginRight: 6, verticalAlign: -1 }} />
              {p.label}
            </button>
          ))}
        </div>
        <label className="ot-pick" data-on={!!props.accent}>
          <input type="color" value={props.accent || '#e8642a'} onChange={(e) => props.onColour({ accent: e.target.value })} aria-label="Pick your own accent colour" />
          Own colour
        </label>
        <span className="ot-swatches" role="group" aria-label="Quick accent colours">
          {QUICK_ACCENTS.map((c) => (
            <button key={c} style={{ background: c }} aria-label={`Accent ${c}`} aria-pressed={props.accent === c} onClick={() => props.onColour({ accent: c })} />
          ))}
        </span>
      </div>
      <div className="ot-tray-row">
        <b>Add a card</b>
        {props.hiddenIds.length ? (
          props.hiddenIds.map((id) => (
            <button key={id} className="ot-chipbtn" onClick={() => props.onAdd(id)}>
              <svg className="ot-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
              {cardLabel(id)}
            </button>
          ))
        ) : (
          <span className="ot-tray-hint">Every card is on the page.</span>
        )}
        <button className="ot-btn sm" onClick={props.onReset} disabled={!props.canReset} style={{ marginLeft: 'auto' }}>Reset to default</button>
      </div>
      {props.canSetDefault ? (
        <div className="ot-tray-row">
          <b>Default</b>
          <button
            className="ot-btn sm dark"
            onClick={async () => {
              setMsg(null);
              try {
                await props.onSaveDefault();
                setMsg({ ok: true, text: 'Saved. Everyone who has not arranged their own unit page now starts from this layout.' });
              } catch (err) {
                console.error('Unit page: set default layout failed', err);
                setMsg({ ok: false, text: "Couldn't save the default. Try again." });
              }
            }}
          >
            Set as default for everyone
          </button>
          <span className="ot-tray-hint" style={{ color: msg ? (msg.ok ? 'var(--ot-good-ink)' : 'var(--ot-bad-ink)') : undefined }}>
            {msg ? msg.text : 'Only you see this. It saves the cards, their order and widths; colours stay personal.'}
          </span>
        </div>
      ) : null}
      <span className="ot-tray-hint">
        {props.stacked
          ? 'Moving and resizing cards works on a wider screen. Colours apply everywhere.'
          : 'Drag a card by its handle to move it, drag its bottom-right corner to change its width, or hide it. Heights fit each unit automatically. Your layout is saved to your account.'}
      </span>
    </div>
  );
}
